import * as Sentry from '@sentry/react';
import { deleteCookie, getCookieValue, trackEvent, useSift } from '@do/seashell';
import { useQuery } from '@do/graphql-client';
import { useLocation, useNavigate } from '@do/seashell/navigation';
import { Box, CheckboxV2 as Checkbox, PropError, style } from '@do/walrus';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import {
  CHURN_SURVEY_URL,
  FLASH_ERROR_COOKIE_NAME,
  GITHUB_STUDENT_PACK,
  GROW_3829_SIGNUP_SELLING_POINTS,
  GROW_3829_SIGNUP_SELLING_POINTS_TREATMENT_A,
  GROW_3829_SIGNUP_SELLING_POINTS_TREATMENT_B,
  ONBOARDING_ORIGIN_PARAM,
  PUBLIC_ENDPOINT,
  REGISTER_ENDPOINT,
  REGISTER_SHOW_OPTIONS_STEP,
} from '../constants';
import copy from '../copy';
import { GetRegistrationInfo } from '../generated-types/GetRegistrationInfo';
import { registerReferralCode, registerReferralCodeVariables } from '../generated-types/registerReferralCode';
import useActivationRedirectCookie from '../hooks/use-activation-redirect-cookie';
import useDocumentTitle from '../hooks/use-document-title';
import useGithubStudentCookie from '../hooks/use-github-student-cookie';
import useOneTimeCookie from '../hooks/use-one-time-cookie';
import { GET_REGISTRATION_FORM_DATA, REGISTER_REFERRAL_CODE } from '../queries/queries';
import { registrationVariants as hardCodedVariants } from '../registration-variants';
import { getRegisterRefcode, setRegisterCookie } from '../utils/get-refcode';
import { getSessionData } from '../utils/gql-utils';
import { getQueryParam, isAmdCampaignRedirect } from '../utils/query-utils';
import Button from './Button';
import { BannerType, Column, HeaderBanner, Loading, Row, Wrap, WrapSplit } from './Common';
import CreditBanner, { AmdCreditBanner } from './CreditBanner';
import CreditText from './CreditText';
import DOLink from './DOLink';
import ErrorBanner from './ErrorBanner';
import FooterWrap from './FooterWrap';
import FormWrap from './FormWrap';
import GithubButton from './GithubButton';
import GoogleButton from './GoogleButton';
import Header from './Header';
import Link from './Link';
import Metadata from './Metadata';
import RegistrationForm from './RegistrationForm';
import { StepTransitionManager } from './StepTransitionManager';
import Text from './Text';
import Title from './Title';
import Wrapper from './Wrapper';
import { registrationTrackingIds } from '../tracking-ids';
import { RegistrationVariantProps } from '../interfaces/registrationVariant';
import { RegistrationVariant } from './registration/registrationVariants/RegistrationVariant';
import { useRegistrationVariants } from '../hooks/use-registration-variants';
import { useRegistrationTemplate } from './RegistrationTemplateProvider';
import PoweredByDigitalOcean from './registration/PoweredByDigitalOcean';
import styled from 'styled-components';
import { StyledButtonProps } from './SharedStyles';
import { isAMD } from '../utils/checkLocationHost';
import DOButton from './DOButton';
import SellingPoints from './SellingPoints';
import { useExperimentVariationAnon } from '@do/seashell/experimentation';
import backgroundsvg from '../assets/signup-layout-background.svg';

const userUUID = getSessionData('ajs_user_id').replace(/(^"|"$)/g, '');
const churnSurvey = userUUID ? `${CHURN_SURVEY_URL}?uuid=${userUUID}` : CHURN_SURVEY_URL;

const { registrationForm } = registrationTrackingIds;

const footerTextColor = {
  dark: style.colors.grey.darkest,
  light: style.colors.white,
};

const SellingPointsBackgroundWrapper = styled.div`
  background: url(${backgroundsvg});
  width: 100vw;
  min-height: 100vh;
  background-size: cover;
  background-position: center;

  @media (min-width: 768px) and (max-width: 1023px) {
    padding: 0 20px;
  }

  @media (max-width: 767px) {
    padding: 0 20px;
  }

  @media (max-width: 374px) {
    padding: 0;
  }
`;

const StyledRow = styled(Row)`
  align-items: center;
  min-height: 100vh;
`;

const StyledFormWrap = styled(FormWrap)`
  margin-top: 2rem;

  @media (max-width: 1024px) {
    margin-top: 7rem;
    margin-left: 2rem;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    width: 400px;
    margin: 7rem auto 0rem;
  }

  @media (max-width: 767px) {
    margin: 7rem auto 0rem;
    padding: 1.5rem 1rem;
  }

  @media (max-width: 643px) {
    padding: 1.5rem 0rem;
  }

  @media (max-width: 515px) {
    width: 95%;
  }

  @media (max-width: 374px) {
    width: 100%;
  }
`;

const StyledTitleWrapper = styled.div`
  h3 {
    font-size: 24px;
  }

  @media (min-width: 516px) {
    h3 {
      font-size: 32px;
    }
  }
`;

const StyledDOLink = styled(DOLink)`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const StyledButton = styled(Button)<StyledButtonProps>(({ buttonStyle }) => {
  return {
    '&&': {
      backgroundColor: buttonStyle?.bgColor,
      color: buttonStyle?.textColor,
      borderColor: buttonStyle?.borderColor,

      '&:hover': {
        borderColor: buttonStyle?.hoverBorderColor,
        backgroundColor: buttonStyle?.hoverBgColor,
        color: buttonStyle?.hoverTextColor,
      },
    },
  };
});

const ConsentCheckboxText = styled.div`
  font-size: ${style.vars.fontSize.small};
  a {
    font-size: ${style.vars.fontSize.small};
  }
`;

type WrapContentProps = {
  children: React.ReactNode;
  data: GetRegistrationInfo | undefined;
  step: number;
  variant?: RegistrationVariantProps;
};

const WrapContent = ({ children, data, step, variant }: WrapContentProps) => {
  const WrapComponent = variant?.splitScreenSplash ? WrapSplit : Wrap;
  const { template } = useRegistrationTemplate();
  const isCreditBanner = !!data?.registrationFormData?.is_credit;
  const isAmdCampaign = isAmdCampaignRedirect();
  return (
    <>
      {isAmdCampaign ? (
        <AmdCreditBanner>
          <strong>{copy.amdCredit.title}</strong>
          <CreditText>{copy.amdCredit.details}</CreditText>
        </AmdCreditBanner>
      ) : isCreditBanner ? (
        <CreditBanner>
          <strong>{copy.credit.title}</strong>
          <CreditText>{copy.credit.details}</CreditText>
        </CreditBanner>
      ) : null}
      <WrapComponent>
        <FormWrap
          className={isCreditBanner ? (variant?.splitScreenSplash ? 'isCreditBannerSplit' : 'isCreditBanner') : ''}
        >
          <Wrapper>
            <StepTransitionManager currentStepNum={step}>
              <Box
                display="block"
                px={style.vars.whitespace.medium}
                {...(template.containerPaddingBottom ? { pb: 2 } : {})}
              >
                {children}
              </Box>
            </StepTransitionManager>
          </Wrapper>
        </FormWrap>
        <FooterWrap
          style={{
            color: footerTextColor[variant?.termsAndPrivacyFooterColor || 'dark'],
          }}
        >
          {template.login.poweredByDigitalOcean && <PoweredByDigitalOcean />}
          {!template.registration.dataSharingConsentRequired && (
            <>
              {copy.footer.terms_before_link + ' '}
              <DOLink path="/legal/terms/" data-tracking-id={registrationTrackingIds.termsOfService}>
                {copy.footer.terms_link_text}
              </DOLink>
              {' ' + copy.footer.privacy_before_link + ' '}
              <DOLink path="/legal/privacy/" data-tracking-id={registrationTrackingIds.privacyPolicy}>
                {copy.footer.privacy_link_text}
              </DOLink>
            </>
          )}
        </FooterWrap>
      </WrapComponent>
    </>
  );
};

const OauthSection: React.FC<{
  googleOauthUrl?: string;
  githubOauthUrl?: string;
}> = ({ googleOauthUrl, githubOauthUrl }) => {
  const GoogleOauthLink = googleOauthUrl ? (
    <Link
      forceExternal={true}
      target="_self"
      to={googleOauthUrl}
      data-tracking-id={registrationForm.inlineGoogleButton}
    >
      {copy.form.oauth.google}
    </Link>
  ) : null;

  const GithubOauthLink = githubOauthUrl ? (
    <Link
      forceExternal={true}
      target="_self"
      to={githubOauthUrl}
      data-tracking-id={registrationForm.inlineGithubButton}
    >
      {copy.form.oauth.github}
    </Link>
  ) : null;

  if (!GoogleOauthLink && !GithubOauthLink) {
    return null;
  }

  return (
    <>
      <Text>
        {copy.form.oauth.title + ' '}
        {googleOauthUrl && (
          <>
            {GoogleOauthLink}
            {' ' + copy.form.or + ' '}
          </>
        )}
        {GithubOauthLink}
        {' ' + copy.form.oauth.instead}
      </Text>
    </>
  );
};

type FlashError = {
  code: string;
  field: string;
};

function Registration({ step = REGISTER_SHOW_OPTIONS_STEP }: { step?: number }): ReactElement {
  const [flashErrorCode, setFlashErrorCode] = useState('');
  const [agreeAmdRegistration, setAgreeAmdRegistration] = useState(false);
  const [buttonClicked, setButtonClicked] = useState(false);
  const flashError = getCookieValue(FLASH_ERROR_COOKIE_NAME);
  const { template } = useRegistrationTemplate();

  useEffect(() => {
    if (flashError) {
      Sentry.captureException(new Error(`Flash Error: ${JSON.stringify(flashError)}`));
      setFlashErrorCode((flashError as unknown as FlashError[])[0]?.code);
      deleteCookie(FLASH_ERROR_COOKIE_NAME);
    }
  }, [flashError]);

  useActivationRedirectCookie();

  const location = useLocation();
  const navigate = useNavigate();
  const refcode = getRegisterRefcode(location.search);
  useQuery<registerReferralCode, registerReferralCodeVariables>(REGISTER_REFERRAL_CODE, {
    variables: { refcode },
    context: {
      url: PUBLIC_ENDPOINT,
    },
    skip: !refcode,
    onError: (error) => {
      Sentry.captureException(error);
      console.error('Error in registering referral code:', error);
    },
  });
  setRegisterCookie(refcode);

  const [sellingPointsVariant, loading] = useExperimentVariationAnon({
    experimentKey: GROW_3829_SIGNUP_SELLING_POINTS,
  });

  const isVariantA = sellingPointsVariant === GROW_3829_SIGNUP_SELLING_POINTS_TREATMENT_A;
  const isVariantB = sellingPointsVariant === GROW_3829_SIGNUP_SELLING_POINTS_TREATMENT_B;
  const showSellingPointsExperiment = (isVariantA || isVariantB) && !refcode;

  const isUserAccountDeactivated = useOneTimeCookie('_digitalocean_flash_code', 'user_account_deactivated');

  const onboardingOrigin = getQueryParam(ONBOARDING_ORIGIN_PARAM, location.search) || '';
  const isGitHubStudentVariant = onboardingOrigin === GITHUB_STUDENT_PACK;
  useGithubStudentCookie(isGitHubStudentVariant);

  const { loading: isLoadingFormData, data } = useQuery<GetRegistrationInfo>(GET_REGISTRATION_FORM_DATA, {
    context: {
      url: REGISTER_ENDPOINT,
    },
    variables: {
      onboarding_origin: onboardingOrigin,
      redirect_url: getQueryParam('redirect_url', location.search),
    },
    onError: (error) => {
      Sentry.captureException(error);
      console.error('Error in getting registration form data:', error);
    },
  });
  const sessionId = data?.registrationFormData?.session_id;
  useSift({
    sessionId: sessionId ?? undefined,
    path: location.pathname,
  });

  const { registrationVariant } = useRegistrationVariants({
    registrationFormData: data,
    isLoadingRegistrationFormData: isLoadingFormData,
    overrideVariant: template?.registration?.variant ? template?.registration?.variant : null,
  });

  useDocumentTitle(registrationVariant?.signUpOptionsForm?.title);

  useEffect(() => {
    if (isLoadingFormData) return;

    trackEvent('Registration Variant', {
      variantType: registrationVariant?.type,
    });
  }, [isLoadingFormData, registrationVariant]);

  const oauthLinkClickHandler = useCallback((url: string) => {
    return async (e: MouseEvent) => {
      e.preventDefault();
      window.location.assign(url);
    };
  }, []);

  if (!registrationVariant || loading) {
    return <Loading />;
  }

  const oauthError = data?.registrationFormData?.oauth_error;
  const googleOauthUrl = `/registrations/google${location.search}`;
  const githubOauthUrl = `/registrations/github${location.search}`;
  const signInUrl = data?.registrationFormData?.marketplace_app?.create_app_url;
  const registrationEmail = data?.registrationFormData?.email ?? undefined;

  let form;
  const errorCode = oauthError || flashErrorCode;
  if (step === REGISTER_SHOW_OPTIONS_STEP) {
    const defaultFormTitle = hardCodedVariants.primary.signUpOptionsForm.title;
    const defaultSubtitle = hardCodedVariants.primary.signUpOptionsForm.subtitle;

    const formHeader = (
      <>
        {errorCode && <ErrorBanner error={copy.errors[errorCode as keyof typeof copy.errors] || copy.errors.unknown} />}
        {showSellingPointsExperiment ? (
          <StyledTitleWrapper>
            <Title title={registrationVariant.signUpOptionsForm?.title || defaultFormTitle} />
          </StyledTitleWrapper>
        ) : (
          <Title title={registrationVariant.signUpOptionsForm?.title || defaultFormTitle} />
        )}

        {registrationVariant.marketplaceAppMetadata ? (
          <Metadata variant={registrationVariant} data={data} />
        ) : (
          <Text>{registrationVariant?.signUpOptionsForm?.subtitle || defaultSubtitle}</Text>
        )}
      </>
    );

    if (registrationVariant.type === 'team' && registrationEmail) {
      form = (
        <>
          {formHeader}
          <OauthSection
            githubOauthUrl={githubOauthUrl}
            googleOauthUrl={template.allowedAuthMethods.includes('google') ? googleOauthUrl : undefined}
          />
          <RegistrationForm
            sessionId={sessionId as string}
            email={registrationEmail}
            onboardingOrigin={onboardingOrigin}
          />
        </>
      );
    } else {
      const emailButton = (
        <StyledButton
          buttonStyle={template.buttons.secondary}
          fullWidth={true}
          onClick={() => {
            setButtonClicked(true);
            if (template.registration.dataSharingConsentRequired && agreeAmdRegistration) {
              navigate(`/registrations/email${location?.search}`);
            } else if (!template.registration.dataSharingConsentRequired) {
              navigate(`/registrations/email${location?.search}`);
            }
          }}
          data-tracking-id={registrationForm.emailButton}
        >
          {copy.form.email_signup_title}
        </StyledButton>
      );

      if (isGitHubStudentVariant) {
        form = (
          <>
            {formHeader}
            <GithubButton
              buttonStyle={template.buttons.secondary}
              dataTrackingId={registrationForm.githubButton}
              text={copy.form.oauth.register.github_button}
              onClick={oauthLinkClickHandler(githubOauthUrl)}
            />
          </>
        );
      } else {
        form = (
          <>
            {formHeader}
            {template.registration.dataSharingConsentRequired && (
              <Box my={style.vars.space[2]}>
                <Checkbox.Input
                  title={
                    <ConsentCheckboxText>
                      {copy.form.dataSharingConsent.termsBeforeLink}{' '}
                      <Link forceExternal={true} to="https://digitalocean.com/legal/terms/">
                        {copy.form.dataSharingConsent.termsLinkText}
                      </Link>
                      ,{' '}
                      <Link forceExternal={true} to="https://digitalocean.com/legal/privacy/">
                        {copy.form.dataSharingConsent.privacyLinkText}
                      </Link>
                      {copy.form.dataSharingConsent.dataSharingBeforeLink}{' '}
                      <Link
                        forceExternal={true}
                        to="https://www.digitalocean.com/legal/privacy-policy#:~:text=(i)%20information%20you%20provide%20through%20your%20user%20account%20on%20the%20Services%20(your%20%E2%80%9CAccount%E2%80%9D)%20if%20you%20register%20for%20the%20Services%3B%20(ii)%20your%20use%20of%20the%20Services%3B%20and%20(iii)%20from%20third%2Dparty%20websites%2C%20services%2C%20and%20partners."
                      >
                        {copy.form.dataSharingConsent.dataSharingLinkText}
                      </Link>{' '}
                      {copy.form.dataSharingConsent.dataSharingAfterLink} *
                    </ConsentCheckboxText>
                  }
                  name="agreeRegistration"
                  value="agreeRegistration"
                  required={true}
                  checked={agreeAmdRegistration}
                  onChange={(e) => {
                    setAgreeAmdRegistration(e.target.checked);
                  }}
                />
                {!agreeAmdRegistration &&
                  buttonClicked &&
                  PropError.renderForAfterInput(copy.form.dataSharingConsentError)}
              </Box>
            )}
            {registrationVariant.type !== 'amd' && (
              <GoogleButton
                text={copy.form.oauth.register.google_button}
                onClick={oauthLinkClickHandler(googleOauthUrl)}
                dataTrackingId={registrationForm.googleButton}
              />
            )}
            <GithubButton
              buttonStyle={template.buttons.primary}
              text={copy.form.oauth.register.github_button}
              onClick={(e) => {
                setButtonClicked(true);
                if (template.registration.dataSharingConsentRequired && agreeAmdRegistration) {
                  oauthLinkClickHandler(githubOauthUrl)(e);
                } else if (!template.registration.dataSharingConsentRequired) {
                  oauthLinkClickHandler(githubOauthUrl)(e);
                }
              }}
              dataTrackingId={registrationForm.githubButton}
            />
            {emailButton}
          </>
        );
      }
    }
  } else {
    form = (
      <>
        <Title title={copy.form.email_signup_title} />
        <OauthSection
          googleOauthUrl={template.allowedAuthMethods.includes('google') ? googleOauthUrl : undefined}
          githubOauthUrl={template.allowedAuthMethods.includes('github') ? githubOauthUrl : undefined}
        />
        <RegistrationForm
          buttonStyle={template.buttons.primary}
          sessionId={sessionId as string}
          email={registrationEmail}
          onboardingOrigin={onboardingOrigin}
        />
      </>
    );
  }

  const showLoginWithDigitalOcean = isAMD() && errorCode === 'email_exists' && step === REGISTER_SHOW_OPTIONS_STEP;
  if (showLoginWithDigitalOcean) {
    const doOauthurl = `/sessions/digitalocean`;
    form = (
      <>
        <Title title={copy.form.loginWithDO.title} />
        <Text secondary>{copy.form.loginWithDO.details1}</Text>
        <Text secondary>{copy.form.loginWithDO.details2}</Text>
        <DOButton
          url={doOauthurl}
          text={copy.form.oauth.login.do_button}
          onClick={() => window.location.assign(doOauthurl)}
          dataTrackingId={registrationTrackingIds.loginForm.doButton}
        />
      </>
    );
  }

  return (
    <>
      <Header forceExternal={!!signInUrl} url={signInUrl} queryString={location.search} />
      {isUserAccountDeactivated && (
        <HeaderBanner
          type={BannerType.Success}
          text={copy.deactivation_message}
          linkText={copy.survey_text}
          url={churnSurvey}
        />
      )}
      {showSellingPointsExperiment ? (
        <>
          <SellingPointsBackgroundWrapper>
            <StyledRow>
              <Column>
                <WrapSplit>
                  <Box display="block" height="fit-content">
                    <StyledFormWrap>
                      <StepTransitionManager currentStepNum={step}>
                        <Box
                          display="block"
                          px={style.vars.whitespace.medium}
                          {...(template.containerPaddingBottom ? { pb: 2 } : {})}
                        >
                          {form}
                        </Box>
                      </StepTransitionManager>
                    </StyledFormWrap>
                  </Box>
                  <FooterWrap
                    style={{
                      color: '#031B4E',
                      fontSize: '12px',
                    }}
                  >
                    {template.login.poweredByDigitalOcean && <PoweredByDigitalOcean />}
                    {!template.registration.dataSharingConsentRequired && (
                      <>
                        {copy.footer.terms_before_link + ' '}
                        <StyledDOLink path="/legal/terms/" data-tracking-id={registrationTrackingIds.termsOfService}>
                          {copy.footer.terms_link_text}
                        </StyledDOLink>
                        {' ' + copy.footer.privacy_before_link + ' '}
                        <StyledDOLink path="/legal/privacy/" data-tracking-id={registrationTrackingIds.privacyPolicy}>
                          {copy.footer.privacy_link_text}
                        </StyledDOLink>
                      </>
                    )}
                  </FooterWrap>
                </WrapSplit>
              </Column>
              <SellingPoints step={step} variant={sellingPointsVariant} />
            </StyledRow>
          </SellingPointsBackgroundWrapper>
        </>
      ) : (
        <RegistrationVariant variant={registrationVariant}>
          <WrapContent data={data} step={step} variant={registrationVariant}>
            {form}
          </WrapContent>
        </RegistrationVariant>
      )}
    </>
  );
}

export default Registration;
